import { formatDistanceToNow } from "date-fns";
import { Calendar, FileText, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	Badge,
	Button,
	Card,
	CardContent,
} from "@/components/primitives";
import { buttonVariants } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

interface DraftReflection {
	id: string;
	title: string;
	description: string;
	updated_at: string;
	qa_results?: any;
}

interface DraftReflectionsListProps {
	onContinueDraft: (draftId: string) => void;
}

export function DraftReflectionsList({
	onContinueDraft,
}: DraftReflectionsListProps) {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [drafts, setDrafts] = useState<DraftReflection[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [draftToDelete, setDraftToDelete] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);

	useEffect(() => {
		if (user) {
			loadDrafts();
		}
	}, [user]);

	const loadDrafts = async () => {
		if (!user) return;

		setLoading(true);
		setError(null);
		try {
			let { data, error } = await supabase
				.from("reflections")
				.select("id, title, description, updated_at, qa_results")
				.eq("user_id", user.id)
				.eq("is_draft", true)
				.eq("archived", false)
				.order("updated_at", { ascending: false });

			if (error) {
				const errorMessage =
					error.message || "Failed to load draft reflections";
				console.error("Error loading drafts:", error);
				setError(errorMessage);
				return;
			}

			setDrafts(data || []);
		} catch (err) {
			console.error("Error loading drafts:", err);
			setError("An unexpected error occurred");
		} finally {
			setLoading(false);
		}
	};

	const getStepFromDraft = (draft: DraftReflection): string => {
		if (!draft.qa_results) return "description";
		try {
			const qaData =
				typeof draft.qa_results === "string"
					? JSON.parse(draft.qa_results)
					: draft.qa_results;

			if (
				qaData?.conversationHistory &&
				Array.isArray(qaData.conversationHistory)
			) {
				const history = qaData.conversationHistory;
				if (history.length > 0) {
					const lastItem = history[history.length - 1];
					if (lastItem.role === "assistant" && !lastItem.answer) {
						return "questions";
					}
				}
			}

			if (
				qaData?.qaHistory &&
				Array.isArray(qaData.qaHistory) &&
				qaData.qaHistory.length > 0
			) {
				return "description";
			}
		} catch (e) {
			console.error("Error parsing qa_results:", e);
		}
		return "description";
	};

	const getStepLabel = (step: string): string => {
		const stepLabels: Record<string, string> = {
			questions: "Questions",
			description: "Description",
			"contributing-factors": "Contributing Factors",
			signals: "Signals",
			"future-state": "Future State",
			"corrective-actions": "Corrective Actions",
			linking: "Linking",
			summary: "Summary",
		};
		return stepLabels[step] || "In progress";
	};

	const handleDeleteClick = (draftId: string) => {
		setDraftToDelete(draftId);
		setDeleteDialogOpen(true);
	};

	const cancelDeleteDraft = () => {
		setDeleteDialogOpen(false);
		setDraftToDelete(null);
	};

	const confirmDeleteDraft = async () => {
		if (!draftToDelete || !user) return;

		setDeleting(true);
		try {
			const { error } = await supabase
				.from("reflections")
				.delete()
				.eq("id", draftToDelete)
				.eq("user_id", user.id);

			if (error) throw error;

			// Remove from local state
			setDrafts(drafts.filter((d) => d.id !== draftToDelete));
			setDeleteDialogOpen(false);
			setDraftToDelete(null);
		} catch (err) {
			console.error("Error deleting draft:", err);
			alert("Failed to delete draft reflection. Please try again.");
		} finally {
			setDeleting(false);
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center py-8">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="bg-red-50 border border-red-200 rounded-lg p-4">
				<div className="flex items-center space-x-2">
					<div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
						<span className="text-white text-xs">!</span>
					</div>
					<div>
						<h4 className="font-medium text-red-800">Error Loading Drafts</h4>
						<p className="text-sm text-red-600 mt-1">{error}</p>
					</div>
				</div>
				<Button
					variant="outline"
					size="sm"
					onClick={loadDrafts}
					className="mt-3"
				>
					Try Again
				</Button>
			</div>
		);
	}

	if (drafts.length === 0) {
		return null; // Don't show anything if there are no drafts
	}

	return (
		<>
			<div className="mt-8 space-y-4">
				<div className="border-t pt-6">
					<h3 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-4">
						Continue Draft Reflections ({drafts.length})
					</h3>
					<div className="space-y-3">
						{drafts.map((draft) => (
							<Card
								key={draft.id}
								className="hover:shadow-md transition-shadow"
								onClick={(e) => {
									// Prevent card click from interfering with button clicks
									if ((e.target as HTMLElement).closest("button")) {
										return;
									}
								}}
							>
								<CardContent className="py-4">
									<div className="flex flex-col sm:flex-row sm:items-start gap-4">
										<div className="flex items-start space-x-4 flex-1 min-w-0">
											<div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center flex-shrink-0">
												<FileText className="h-6 w-6 text-orange-600" />
											</div>
											<div className="flex-1 min-w-0">
												<div className="flex items-center space-x-2 mb-1">
													<h3 className="font-semibold truncate">
														{draft.title || "Untitled Reflection"}
													</h3>
													<Badge variant="outline" className="text-xs">
														Draft
													</Badge>
												</div>
												<p className="text-sm text-gray-600 dark:text-gray-400 mb-2 break-words">
													{draft.description || "No description yet"}
												</p>
												<div className="flex items-center space-x-4 text-xs text-gray-500">
													<div className="flex items-center space-x-1">
														<Calendar className="h-3 w-3" />
														<span>
															{formatDistanceToNow(new Date(draft.updated_at), {
																addSuffix: true,
															})}
														</span>
													</div>
													<div className="flex items-center space-x-1">
														<span>
															Step: {getStepLabel(getStepFromDraft(draft))}
														</span>
													</div>
												</div>
											</div>
										</div>
										<div className="flex items-center space-x-2 flex-shrink-0 sm:self-start">
											<Button
												data-testid="delete-draft-button"
												variant="ghost"
												size="sm"
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													handleDeleteClick(draft.id);
												}}
												className="text-gray-500 hover:text-red-600"
											>
												<Trash2 className="h-4 w-4" />
											</Button>
											<Button
												data-testid="continue-draft-button"
												type="button"
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													console.log(
														"Continue button clicked for draft:",
														draft.id,
													);
													// Try both the callback and direct navigation as fallback
													if (
														onContinueDraft &&
														typeof onContinueDraft === "function"
													) {
														try {
															onContinueDraft(draft.id);
														} catch (error) {
															console.error(
																"Error calling onContinueDraft:",
																error,
															);
															// Fallback to direct navigation
															navigate(`/reflection/continue/${draft.id}`);
														}
													} else {
														console.warn(
															"onContinueDraft not available, using direct navigation",
														);
														navigate(`/reflection/continue/${draft.id}`);
													}
												}}
												size="sm"
												className="relative z-10"
											>
												Continue
											</Button>
										</div>
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				</div>
			</div>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete Draft Reflection</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete this draft reflection? This action
							cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={cancelDeleteDraft}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							className={buttonVariants({ variant: "destructive" })}
							onClick={confirmDeleteDraft}
							disabled={deleting}
						>
							{deleting ? "Deleting..." : "Delete"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
